import React, { useState, useEffect } from 'react';
import { useToast, type Toast } from '../../context/ToastContext';

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [, setIsPaused] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = () => {
    setIsVisible(false);
    // Wait for exit animation before removing
    setTimeout(() => onRemove(toast.id), 300);
  };

  const getToastIcon = () => {
    const iconClass = "w-5 h-5 flex-shrink-0";
    
    switch (toast.type) {
      case 'success':
        return (
          <div className="flex-shrink-0">
            <svg className={`${iconClass} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="flex-shrink-0">
            <svg className={`${iconClass} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="flex-shrink-0">
            <svg className={`${iconClass} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case 'info':
        return (
          <div className="flex-shrink-0">
            <svg className={`${iconClass} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getToastClasses = () => {
    const baseClasses = "cs-toast flex items-start p-4 mb-3 rounded-lg shadow-lg border-l-4 transition-all duration-300 ease-out max-w-md w-full";
    const visibilityClasses = isVisible 
      ? "opacity-100 translate-x-0 scale-100" 
      : "opacity-0 translate-x-full scale-95";
    
    const typeClasses = {
      success: "border-l-green-500 bg-white dark:bg-gray-800",
      error: "border-l-red-500 bg-white dark:bg-gray-800",
      warning: "border-l-yellow-500 bg-white dark:bg-gray-800", 
      info: "border-l-blue-500 bg-white dark:bg-gray-800"
    };

    return `${baseClasses} ${visibilityClasses} ${typeClasses[toast.type]}`;
  };

  return (
    <div 
      className={getToastClasses()}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {getToastIcon()}
      
      <div className="ml-3 flex-1 min-w-0">
        {toast.title && (
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {toast.title}
          </p>
        )}
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          {toast.message}
        </p>
        
        {toast.action && (
          <div className="mt-3">
            <button
              onClick={() => {
                toast.action!.onClick();
                handleRemove();
              }}
              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200"
            >
              {toast.action.label}
            </button>
          </div>
        )}
      </div>

      {toast.dismissible && (
        <button
          onClick={handleRemove}
          className="ml-4 inline-flex text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus:text-gray-600 dark:focus:text-gray-200 transition-colors duration-200"
          aria-label="Close notification"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const { toasts, removeToast, addToast } = useToast();

  // Listen for custom toast events from hooks and other parts of the app
  useEffect(() => {
    const handleShowToast = (event: CustomEvent) => {
      const { type, message, title, duration, action } = event.detail;
      addToast({
        type: type || 'info',
        message,
        title,
        duration,
        action,
      });
    };

    window.addEventListener('show-toast', handleShowToast as EventListener);
    
    return () => {
      window.removeEventListener('show-toast', handleShowToast as EventListener);
    };
  }, [addToast]);

  if (toasts.length === 0) return null;

  return (
    <div 
      className="fixed top-4 right-4 flex flex-col items-end space-y-2 pointer-events-none"
      style={{ zIndex: 'var(--cs-z-toast)' }}
    >
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onRemove={removeToast} />
        </div>
      ))}
    </div>
  );
};